import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AccountStatusProvider } from './context/AccountStatusContext';
import { BlockedAccountGate } from './components/BlockedAccountGate';
import { Layout, LoadingPage } from './components/Layout';
import { authApi } from './services/api';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { OrdersPage } from './pages/OrdersPage';
import { NewOrderPage } from './pages/NewOrderPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { EditOrderPage } from './pages/EditOrderPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import { ProfilePage } from './pages/ProfilePage';
import { QuoteCalculatorPage } from './pages/QuoteCalculatorPage';
import { PartsPage } from './pages/PartsPage';
import { InstallResourcesPage } from './pages/warranty/InstallResourcesPage';
import { WarrantyTermsPage } from './pages/warranty/WarrantyTermsPage';
import { RegisterWarrantyPage } from './pages/warranty/RegisterWarrantyPage';
import { MyWarrantiesPage } from './pages/warranty/MyWarrantiesPage';
import { SubmitRmaPage } from './pages/warranty/SubmitRmaPage';
import { MyRmasPage } from './pages/warranty/MyRmasPage';
import { CadDrawingsPage } from './pages/CadDrawingsPage';
import { MarketingResourcesPage } from './pages/MarketingResourcesPage';
import { SupportPage } from './pages/SupportPage';
import MyLeadsPage from './pages/MyLeadsPage';
import { CalendarPage } from './pages/CalendarPage';
import { TeamManagementPage } from './pages/admin/TeamManagementPage';
import { CommissionSettingsPage } from './pages/admin/CommissionSettingsPage';
import { CommissionReportsPage } from './pages/admin/CommissionReportsPage';
import { ActivityLogPage } from './pages/admin/ActivityLogPage';
import { SalesOverviewPage } from './pages/admin/SalesOverviewPage';
import { MyCommissionsPage } from './pages/MyCommissionsPage';
import RewardsPage from './pages/RewardsPage';
import { ScreenDesignerPage } from './pages/ScreenDesignerPage';
import { PriceBuilderPage } from './pages/PriceBuilderPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import { InvoicesPage } from './pages/InvoicesPage';
import MyShipmentsPage from './pages/MyShipmentsPage';
// v2: import VisualOrderPage from './pages/VisualOrderPage';

// Terms Modal - Shows only once on first login
function TermsModal({ onAccept }: { onAccept: () => void }) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!agreed) return;
    setSubmitting(true);
    try {
      await authApi.acceptTerms();
      onAccept();
    } catch (err) {
      console.error('Failed to accept terms:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-keder-dark border border-keder-gray rounded-lg max-w-lg w-full p-6">
        <h2 className="text-xl font-bold text-white mb-4">Welcome to Keder Dealer Portal</h2>
        <p className="text-gray-300 mb-4">
          By using this portal you agree to our{' '}
          <a href="https://kederscreens.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-keder-yellow hover:underline">
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a href="https://kederscreens.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-keder-yellow hover:underline">
            Privacy Policy
          </a>.
        </p>

        {/* Customer Sell Price Data Privacy Notice */}
        <div className="bg-keder-gray/30 border border-keder-gray rounded-lg p-4 mb-6 max-h-48 overflow-y-auto">
          <h3 className="text-sm font-semibold text-keder-yellow mb-2">Your Sell Price Data</h3>
          <p className="text-xs text-gray-400 leading-relaxed">
            When you optionally enter your customer sell price, this data is:
          </p>
          <ul className="text-xs text-gray-400 mt-2 space-y-1 list-disc list-inside">
            <li>Used solely for aggregate market analysis</li>
            <li>Used for your internal commission tracking (if enabled)</li>
            <li>Never disclosed to end customers</li>
            <li>Never shared with other dealers</li>
            <li>Never used to undercut dealer pricing</li>
          </ul>
          <p className="text-xs text-gray-400 mt-2">
            We respect that dealers set their own pricing strategies. Your individual pricing is confidential.
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="w-5 h-5 rounded border-gray-500 bg-keder-dark text-keder-yellow focus:ring-keder-yellow focus:ring-2"
          />
          <span className="text-white">I agree to the Terms of Service and Privacy Policy</span>
        </label>
        <button
          onClick={handleAccept}
          disabled={!agreed || submitting}
          className="w-full py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Please wait...' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !termsChecked) {
      authApi.checkTermsStatus()
        .then(({ accepted }) => {
          setShowTermsModal(!accepted);
          setTermsChecked(true);
        })
        .catch(() => {
          // On error, assume terms are accepted
          setTermsChecked(true);
        });
    }
  }, [isAuthenticated, termsChecked]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while checking terms
  if (!termsChecked) {
    return <LoadingPage />;
  }

  return (
    <>
      {showTermsModal && (
        <TermsModal onAccept={() => setShowTermsModal(false)} />
      )}
      <Layout>
        <BlockedAccountGate>
          <Outlet />
        </BlockedAccountGate>
      </Layout>
    </>
  );
}

// Public route - redirects to dashboard if authenticated
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingPage />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

// Admin impersonation — accepts ?token= param, sets auth, redirects to dashboard
function ImpersonatePage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setError('No token provided');
      return;
    }
    localStorage.setItem('auth_token', token);
    refreshUser().then(() => {
      navigate('/dashboard', { replace: true });
    }).catch(() => {
      setError('Invalid or expired token');
      localStorage.removeItem('auth_token');
    });
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-gray-900 p-8 rounded-lg text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="text-yellow-400 underline">Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto mb-4" />
        <p className="text-gray-400">Logging in...</p>
      </div>
    </div>
  );
}

/**
 * Magic-link verify page — handles `?token=<...>` from the Pay Online CTA in
 * invoice emails (and any future passwordless flows). Calls /auth/verify,
 * stores the returned JWT, then redirects to the `redirectTo` path from the
 * response (e.g. `/orders/<id>`) or `/` if none.
 */
function AuthVerifyPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setError('No token provided');
      return;
    }
    (async () => {
      try {
        // Import inside effect to avoid a top-level circular dep concern.
        const { authApi } = await import('./services/api');
        const result = await authApi.verifyMagicLink(token);
        localStorage.setItem('auth_token', result.token);
        await refreshUser();
        navigate(result.redirectTo || '/', { replace: true });
      } catch {
        setError('This link is invalid or has expired. Please log in to view the order.');
        localStorage.removeItem('auth_token');
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="bg-gray-900 p-8 rounded-lg text-center max-w-md">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="text-yellow-400 underline">Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto mb-4" />
        <p className="text-gray-400">Opening your order…</p>
      </div>
    </div>
  );
}

// Router component
function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Admin impersonation entry point */}
      <Route path="/impersonate" element={<ImpersonatePage />} />

      {/* Magic-link entry point — Pay Online CTAs in invoice emails land here.
          Public route (no ProtectedRoute wrapper) since the dealer isn't
          logged in yet; the page itself handles the auth handshake. */}
      <Route path="/auth/verify" element={<AuthVerifyPage />} />

      {/* Protected routes with layout */}
      <Route path="/" element={<ProtectedRoute />}>
        <Route index element={<DashboardPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="shipments" element={<MyShipmentsPage />} />
        <Route path="installs" element={<CalendarPage />} />
        <Route path="leads" element={<MyLeadsPage />} />
        <Route path="orders/new" element={<NewOrderPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="orders/:id/edit" element={<EditOrderPage />} />
        <Route path="orders/:orderId/confirmation" element={<OrderConfirmationPage />} />
        <Route path="orders/:orderId/payment-cancelled" element={<PaymentCancelledPage />} />
        <Route path="quote" element={<QuoteCalculatorPage />} />
        <Route path="price-builder" element={<PriceBuilderPage />} />
        <Route path="designer" element={<ScreenDesignerPage />} />
        {/* v2: <Route path="visual-order" element={<VisualOrderPage />} /> */}
        <Route path="email-templates" element={<EmailTemplatesPage />} />
        <Route path="parts" element={<PartsPage />} />
        <Route path="my-commissions" element={<MyCommissionsPage />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="install-resources" element={<InstallResourcesPage />} />
        <Route path="cad-drawings" element={<CadDrawingsPage />} />
        <Route path="marketing-resources" element={<MarketingResourcesPage />} />
        <Route path="warranty/terms" element={<WarrantyTermsPage />} />
        <Route path="warranty/register" element={<RegisterWarrantyPage />} />
        <Route path="warranty/my-warranties" element={<MyWarrantiesPage />} />
        <Route path="warranty/submit-rma" element={<SubmitRmaPage />} />
        <Route path="warranty/my-rmas" element={<MyRmasPage />} />
        <Route path="support" element={<SupportPage />} />
        {/* Admin routes */}
        <Route path="admin/team" element={<TeamManagementPage />} />
        <Route path="admin/commission-settings" element={<CommissionSettingsPage />} />
        <Route path="admin/commission-reports" element={<CommissionReportsPage />} />
        <Route path="admin/sales-overview" element={<SalesOverviewPage />} />
        <Route path="admin/activity-log" element={<ActivityLogPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App
function App() {
  return (
    <AuthProvider>
      <AccountStatusProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AccountStatusProvider>
    </AuthProvider>
  );
}

export default App;
